<script setup lang="ts" name="RegistrationTradesSearch">
import { computed, ref, watch } from 'vue';
import { useVModel } from '@vueuse/core';
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core';

import {
  ALERT_TYPE,
  REGISTRATION_MAXIMUM_TRADES,
  REGISTRATION_SEARCH_OPTION_TYPE,
} from '~/constants';

import { provideBaseCardState } from '~/features/useBaseCardState';
import {
  getRegistrationFilteredOptions,
  getRegistrationFilteredSelectedOptions,
  getRegistrationFilteredSelectedTrades,
  getRegistrationHighlightedOptions,
  getRegistrationSelectedOptionsByTrade,
  getUniqueRegistrationSearchTradeLanes,
} from '~/features/useRegistrationSearch';
import { useSearchDebounce } from '~/features/useSearchDebounce';
import { isEmpty } from '~/features/useUtilities';

import Service from '~/services/Service.js';

import BaseAlert from '~/components/BaseAlert.vue';
import FilterMenuOptions from '~/components/FilterMenuOptions.vue';
import RegistrationSelectedOptions from '~/components/RegistrationSelectedOptions.vue';
import RegistrationSelectedTrades from '~/components/RegistrationSelectedTrades.vue';
import RegistrationShippingTradesList from '~/components/RegistrationShippingTradesList.vue';
import RegistrationTradesSearchCount from '~/components/RegistrationTradesSearchCount.vue';
import RegistrationTradesSearchOverlay from '~/components/RegistrationTradesSearchOverlay.vue';
import SearchField from '~/components/SearchField.vue';
import XDropdown from '~/components/XDropdown.vue';

import type {
  IRegistrationHighlightedOption,
  IRegistrationSearchOptionType,
  IRegistrationTradelanesOption,
  IRegistrationTradelanesResponse,
  IStatisticsTradeLane,
  ITradeLane,
} from '~/types';

// TODO: Create Field Element Component from RegistrationTradesSearch

const props = withDefaults(
  defineProps<{
    modelValue: IRegistrationHighlightedOption[];
    error: boolean;
    isFullWidth?: boolean;
    variant?: 'minimal' | 'default';
  }>(),
  {
    variant: 'default',
    isFullWidth: false,
  },
);

const emit = defineEmits<{
  (event: 'update:modelValue', value: IRegistrationHighlightedOption[]): void;
  (event: 'updateErrorState'): void;
}>();

const { setStateLoading, setStateIdle, stateIsLoading } =
  provideBaseCardState();

const MIN_SEARCH_LENGTH = 2;

const inputValue = useVModel(props, 'modelValue', emit);

const search = ref('');
const overlay = ref(false);

const tradelanes = ref<ITradeLane[]>([]);
const countries = ref<IRegistrationTradelanesOption[]>([]);
const ports = ref<IRegistrationTradelanesOption[]>([]);

const selectedOptions = ref<IRegistrationHighlightedOption[]>(
  inputValue.value ?? [],
);

const selectedSearchedOptions = computed<IRegistrationHighlightedOption[]>(() =>
  selectedOptions.value.filter(
    (option) =>
      option.searched && option.type !== REGISTRATION_SEARCH_OPTION_TYPE.TRADES,
  ),
);

const selectedOptionsTrades = computed<IStatisticsTradeLane[]>(() =>
  getUniqueRegistrationSearchTradeLanes(selectedOptions.value),
);

const alertText = computed(() => {
  const exceededTradeLimitCount =
    selectedOptionsTrades.value.length - REGISTRATION_MAXIMUM_TRADES;

  return `<b> You’ve have hit the limit of trades. </b>
      Please remove ${exceededTradeLimitCount} ${
        exceededTradeLimitCount === 1 ? 'trade' : 'trades'
      } in order to proceed.`;
});

const filterOptionsByType = (type: IRegistrationSearchOptionType) =>
  selectedOptions.value.filter((option) => option.type === type);

const tradelanesOptions = computed<IRegistrationHighlightedOption[]>(() => {
  if (search.value.length < MIN_SEARCH_LENGTH) {
    return [];
  }

  const options = filterOptionsByType('Trades');

  const filteredOptions = getRegistrationFilteredOptions(
    tradelanes.value,
    options,
    search.value,
    true,
  );

  return getRegistrationHighlightedOptions(
    REGISTRATION_SEARCH_OPTION_TYPE.TRADES,
    filteredOptions,
    search.value,
  );
});

const countriesOptions = computed<IRegistrationHighlightedOption[]>(() => {
  if (search.value.length < MIN_SEARCH_LENGTH) {
    return [];
  }

  const options = filterOptionsByType('Countries');

  const filteredOptions = getRegistrationFilteredOptions(
    countries.value,
    options,
    search.value,
  );

  return getRegistrationHighlightedOptions(
    REGISTRATION_SEARCH_OPTION_TYPE.COUNTRIES,
    filteredOptions,
    search.value,
  );
});

const portsOptions = computed<IRegistrationHighlightedOption[]>(() => {
  if (search.value.length < MIN_SEARCH_LENGTH) {
    return [];
  }

  const options = filterOptionsByType('Ports');

  const filteredOptions = getRegistrationFilteredOptions(
    ports.value,
    options,
    search.value,
  );

  return getRegistrationHighlightedOptions(
    REGISTRATION_SEARCH_OPTION_TYPE.PORTS,
    filteredOptions,
    search.value,
  );
});

const isStateEmpty = computed(
  () =>
    isEmpty(tradelanesOptions.value) &&
    isEmpty(countriesOptions.value) &&
    isEmpty(portsOptions.value),
);

const fetchTradelanes = () => {
  Service.tradelanes(search.value)
    .onStart(setStateLoading)
    .onSuccess(({ data }: IRegistrationTradelanesResponse) => {
      tradelanes.value = data.tradelanes;
      countries.value = data.countries;
      ports.value = data.ports;

      setStateIdle();
    })
    .execute();
};

const isOnDesktop = computed(() =>
  useBreakpoints(breakpointsTailwind).isGreater('lg'),
);

const clearFilter = (option: IRegistrationHighlightedOption) => {
  selectedOptions.value = getRegistrationFilteredSelectedOptions(
    selectedOptions.value,
    option,
  );
};

const clearTrade = (optionId: IRegistrationHighlightedOption['id']) => {
  selectedOptions.value = getRegistrationFilteredSelectedTrades(
    selectedOptions.value,
    optionId,
  );
};

const selectOption = (
  close: () => void,
  selectedOption: IRegistrationHighlightedOption,
) => {
  const option = {
    ...selectedOption,
    searched: true,
  };

  selectedOptions.value = [...selectedOptions.value, option];

  const allOptions = [
    ...tradelanesOptions.value,
    ...portsOptions.value,
    ...countriesOptions.value,
  ];

  const selectedAllOptions = getRegistrationSelectedOptionsByTrade(
    allOptions,
    selectedOptionsTrades.value,
  );

  selectedOptions.value = [...selectedOptions.value, ...selectedAllOptions];

  close();
};

const showDialog = (
  searchValue: string,
  open: () => void,
  close: () => void,
) => {
  const shouldClose = searchValue.length < MIN_SEARCH_LENGTH;

  if (shouldClose) {
    close();

    return;
  }

  open();
};

const showOverlay = () => {
  overlay.value = true;
};

const onSearchClick = (
  searchValue: string,
  open: () => void,
  close: () => void,
) => {
  if (!isOnDesktop.value) {
    showOverlay();
    return;
  }

  showDialog(searchValue, open, close);
};

watch(
  () => selectedOptionsTrades.value,
  (currTrades) => {
    if (props.error && currTrades.length <= REGISTRATION_MAXIMUM_TRADES) {
      emit('updateErrorState');
    }
  },
);

watch(
  () => selectedOptions.value,
  (val) => {
    inputValue.value = val;
  },
);

useSearchDebounce(search, fetchTradelanes, MIN_SEARCH_LENGTH);
</script>

<template>
  <div
    :class="[
      'flex flex-col',
      variant === 'minimal' ? 'items-start' : 'items-center',
    ]"
  >
    <BaseAlert v-if="error" :type="ALERT_TYPE.ERROR" class="mx-4 mb-4">
      <p v-html="alertText" />
    </BaseAlert>

    <div :class="isFullWidth ? 'w-full' : 'w-11/12 md:w-1/2'">
      <div>
        <XDropdown data-testid="semi-public-search">
          <template #trigger="{ open, close }">
            <SearchField
              v-model="search"
              placeholder="Search by Port, Trade or Country"
              :error="error"
              :isolated="isOnDesktop ? false : true"
              @click="onSearchClick(search, open, close)"
              @update:modelValue="
                (value: string) =>
                  isOnDesktop ? onSearchClick(value, open, close) : {}
              "
            />
          </template>

          <template v-if="!stateIsLoading && isOnDesktop" #default="{ close }">
            <FilterMenuOptions
              data-testid="combo-box-options"
              :hasOptions="!isStateEmpty"
            >
              <RegistrationShippingTradesList
                :portsOptions="portsOptions"
                :countriesOptions="countriesOptions"
                :tradelanesOptions="tradelanesOptions"
                @update:model-value="selectOption(close, $event)"
              />
            </FilterMenuOptions>
          </template>
        </XDropdown>

        <RegistrationTradesSearchCount
          v-if="selectedOptionsTrades.length && variant === 'default'"
          :count="selectedOptionsTrades.length"
          :error="error"
        />
      </div>
    </div>

    <div
      :class="[
        'flex flex-col items-center',
        variant === 'default' && 'mx-6 mt-6',
      ]"
    >
      <RegistrationSelectedOptions
        v-if="selectedSearchedOptions.length && variant === 'default'"
        :selected-options="selectedSearchedOptions"
        @clearFilter="clearFilter"
        :variant="variant"
      />
      <RegistrationSelectedTrades
        v-if="selectedOptionsTrades.length"
        :selectedTrades="selectedOptionsTrades"
        :variant="variant"
        :title="
          variant === 'minimal' ? '' : 'Your results are displayed as trades:'
        "
        @clearTrade="clearTrade"
      />
    </div>

    <RegistrationTradesSearchOverlay
      v-model:overlay="overlay"
      v-model:search="search"
      :error="error"
      :stateEmpty="isStateEmpty"
      :loading="stateIsLoading"
      :selectedTrades="selectedOptionsTrades.length"
      :portsOptions="portsOptions"
      :countriesOptions="countriesOptions"
      :tradelanesOptions="tradelanesOptions"
      @update:options="selectOption(() => (overlay = false), $event)"
    />
  </div>
</template>
